import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  Checkbox,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  fetchPrograms,
  getPolicyByProgramAndLabel,
  editPolicy,
  getPolicyByLabelId, // <-- add this import
  deletePolicy,
} from "../api/faqApi";
import { ProgramOption } from "../api/types";
import debounce from "lodash/debounce";
import EditPolicyDialog from "../components/Popups/EditPolicyDialog";
import { downloadPoliciesPdf } from "../utils/downloadPoliciesPdf";
import ConfirmDialog from "../components/ConfirmDialog";
import CreatePolicyDialog from "../components/Popups/CreatePolicyDialog";

const PolicyDashboard: React.FC = () => {
  const [ppkProgramOptions, setPpkProgramOptions] = useState<ProgramOption[]>(
    []
  );
  const [selectedPpkPrograms, setSelectedPpkPrograms] = useState<
    ProgramOption[]
  >([]);
  const [ppkSearchValue, setPpkSearchValue] = useState("");
  const [isPpkDropdownOpen, setIsPpkDropdownOpen] = useState(false);
  const [loadingPpkPrograms, setLoadingPpkPrograms] = useState(false);
  const [programPoliciesMap, setProgramPoliciesMap] = useState<{
    [programCode: string]: any[];
  }>({});
  const [showPoliciesMap, setShowPoliciesMap] = useState<{
    [programCode: string]: boolean;
  }>({});
  const [loadingPoliciesMap, setLoadingPoliciesMap] = useState<{
    [programCode: string]: boolean;
  }>({});

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [editDialogLoading, setEditDialogLoading] = useState(false);
  const [editDialogFetchLoading, setEditDialogFetchLoading] = useState(false); // <-- add this state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Add state for delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    programCode: string;
    policy_id: string;
  } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Debounced function for fetching programs
  const debouncedPpkProgramSearch = useCallback(
    debounce(async (query: string) => {
      setLoadingPpkPrograms(true);
      try {
        const programs = await fetchPrograms(query);
        setPpkProgramOptions(programs);
      } catch (error) {
        setPpkProgramOptions([]);
      } finally {
        setLoadingPpkPrograms(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (isPpkDropdownOpen) {
      debouncedPpkProgramSearch(ppkSearchValue);
    }
    return () => {
      debouncedPpkProgramSearch.cancel();
    };
  }, [ppkSearchValue, isPpkDropdownOpen, debouncedPpkProgramSearch]);

  // Add state for edit dialog and current policy
  const [editPolicyDialogOpen, setEditPolicyDialogOpen] = useState(false);
  const [editPolicyDialogInitialValues, setEditPolicyDialogInitialValues] =
    useState<{
      label: string;
      description: string;
      program_code: string;
      label_id: string;
      programName: string;
      policy_id: string;
    } | null>(null);

  // Update handleEditClick to open EditPolicyDialog
  const handleEditClick = async (programCode: string, idx: number) => {
    const policy = programPoliciesMap[programCode][idx];
    // Find the program label from selectedPpkPrograms
    const programObj = selectedPpkPrograms.find(
      (p) => p.code === programCode || p.value === programCode
    );
    const programName = programObj?.label || "";
    setEditDialogFetchLoading(true);
    try {
      const apiRes = await getPolicyByLabelId({
        policy_id: policy.policy_id,
      });
      const apiPolicy = apiRes?.output?.policy;
      if (!apiPolicy) {
        setSnackbar({
          open: true,
          message: "Failed to fetch policy details.",
          severity: "error",
        });
        return;
      }
      setEditPolicyDialogInitialValues({
        label: apiPolicy.label,
        description: apiPolicy.description,
        program_code: programCode,
        label_id: apiPolicy.label_id,
        programName,
        policy_id: apiPolicy.policy_id,
      });
      setEditPolicyDialogOpen(true);
      // setCurrentEditProgramCode(programCode); // This line was removed
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to fetch policy details.",
        severity: "error",
      });
    } finally {
      setEditDialogFetchLoading(false);
    }
  };

  // Add state to track which program is being edited
  const [currentEditProgramCode, setCurrentEditProgramCode] = useState<
    string | null
  >(null);

  const handlePolicyCreated = (programCode: string) => {
    // Only refresh if the program is currently selected
    if (
      selectedPpkPrograms.some(
        (p) => p.code === programCode || p.value === programCode
      )
    ) {
      setLoadingPoliciesMap((prev) => ({
        ...prev,
        [programCode]: true,
      }));
      getPolicyByProgramAndLabel({ program_code: programCode })
        .then((res) => {
          setProgramPoliciesMap((prev) => ({
            ...prev,
            [programCode]: res.output?.policy || [],
          }));
        })
        .catch(() => {
          setProgramPoliciesMap((prev) => ({
            ...prev,
            [programCode]: [],
          }));
        })
        .finally(() => {
          setLoadingPoliciesMap((prev) => ({
            ...prev,
            [programCode]: false,
          }));
        });
    }
  };

  const handlePolicyUpdated = (programCode: string) => {
    // Only refresh if the program is currently selected
    if (
      selectedPpkPrograms.some(
        (p) => p.code === programCode || p.value === programCode
      )
    ) {
      setLoadingPoliciesMap((prev) => ({
        ...prev,
        [programCode]: true,
      }));
      getPolicyByProgramAndLabel({ program_code: programCode })
        .then((res) => {
          setProgramPoliciesMap((prev) => ({
            ...prev,
            [programCode]: res.output?.policy || [],
          }));
        })
        .catch(() => {
          setProgramPoliciesMap((prev) => ({
            ...prev,
            [programCode]: [],
          }));
        })
        .finally(() => {
          setLoadingPoliciesMap((prev) => ({
            ...prev,
            [programCode]: false,
          }));
        });
    }
  };

  return (
    <Box sx={{}}>
      {/* Policy Title and Create Policy Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          bgcolor: "#fafafa",
          zIndex: 1,
          pt: 1,
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, fontSize: "16px", color: "#000" }}
        >
          Policy
        </Typography>
        <Button
          variant="contained"
          sx={{
            border: "1px solid #000",
            bgcolor: "#fff",
            color: "#000",
            boxShadow: "none",
            borderRadius: "6px",
            px: 3,
            py: 1,
            fontSize: 12,
            fontWeight: 600,
            textTransform: "none",
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setCreateDialogOpen(true);
          }}
        >
          Create Policy
        </Button>
      </Box>
      <CreatePolicyDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
        }}
        initialValues={{ program: "", course: "", description: "" }}
        onPolicyCreated={(programCode) => {
          setCreateDialogOpen(false);
          handlePolicyCreated(programCode);
        }}
      />
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: 3,
          p: 4,
          minHeight: 520,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Typography sx={{ minWidth: 160 }}>Search for Program</Typography>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              multiple
              id="policy-program-select"
              open={isPpkDropdownOpen}
              onOpen={() => setIsPpkDropdownOpen(true)}
              onClose={() => setIsPpkDropdownOpen(false)}
              options={ppkProgramOptions || []}
              value={selectedPpkPrograms || []}
              loading={loadingPpkPrograms}
              inputValue={ppkSearchValue}
              onInputChange={(event, newValue) => {
                setPpkSearchValue(newValue);
              }}
              onChange={(event, newValue: ProgramOption[]) => {
                setSelectedPpkPrograms(newValue || []);
                const newPoliciesMap: { [programCode: string]: any[] } = {};
                const newLoadingMap: { [programCode: string]: boolean } = {};

                (newValue || []).forEach((program) => {
                  newLoadingMap[program.code || program.value] = true;
                  getPolicyByProgramAndLabel({
                    program_code: program.code || program.value,
                  })
                    .then((res) => {
                      setProgramPoliciesMap((prev) => ({
                        ...prev,
                        [program.code || program.value]:
                          res.output?.policy || [],
                      }));
                    })
                    .catch(() => {
                      setProgramPoliciesMap((prev) => ({
                        ...prev,
                        [program.code || program.value]: [],
                      }));
                    })
                    .finally(() => {
                      setLoadingPoliciesMap((prev) => ({
                        ...prev,
                        [program.code || program.value]: false,
                      }));
                    });
                });

                setProgramPoliciesMap(newPoliciesMap);
                setLoadingPoliciesMap(newLoadingMap);
              }}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    selectedPpkPrograms?.length > 0
                      ? `${selectedPpkPrograms[0]?.label || ""}${
                          selectedPpkPrograms.length > 1
                            ? ` +${selectedPpkPrograms.length - 1}`
                            : ""
                        }`
                      : "Search & Select Programs"
                  }
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: null,
                    endAdornment: params.InputProps.endAdornment,
                    sx: {
                      width: "50%",
                      background: "#f9fbfc",
                      borderRadius: "8px",
                      border: "1px solid #d3e1e6",
                      boxSizing: "border-box",
                      height: "48px",
                      padding: 0,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "16px",
                      fontWeight: 400,
                      padding: "12px 14px",
                      height: "48px",
                      position: "relative",
                      top: "-17px",
                    },
                    "& input::placeholder": {
                      color: "#bdbdbd",
                      opacity: 1,
                      fontSize: "16px",
                      fontWeight: 400,
                    },
                  }}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      checked={selected}
                      sx={{ mr: 1 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">
                        {option?.label}{" "}
                        <span style={{ fontSize: "14px", color: "#777" }}>
                          ({option?.code})
                        </span>
                      </Typography>
                      {option?.pok && option.pok.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", display: "block" }}
                        >
                          POKs:{" "}
                          {Array.isArray(option.pok)
                            ? option.pok.join(", ")
                            : option.pok}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </li>
              )}
            />
          </Box>
        </Box>

        {/* Program Info Box */}
        <Box sx={{ maxHeight: 500, overflowY: "auto" }}>
          {selectedPpkPrograms.map((program, idx) => {
            const code = program.code || program.value;
            const isFirst = idx === 0;
            const isLast = idx === selectedPpkPrograms.length - 1;
            return (
              <Accordion
                key={code}
                expanded={!!showPoliciesMap[code]}
                sx={{
                  "&&": {
                    marginLeft: "10px",
                    marginRight: "10px",
                  },
                  boxShadow: 1,
                  bgcolor: "#f9fbfc",
                  mx: "10px",
                  borderTop: isFirst ? "1px solid #e0e0e0" : undefined,
                  borderBottom: isLast ? "1px solid #e0e0e0" : undefined,
                }}
                onChange={() => {
                  setShowPoliciesMap((prev) => ({
                    ...prev,
                    [code]: !prev[code],
                  }));
                }}
              >
                <AccordionSummary
                  sx={{
                    minHeight: 48,
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    },
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                    mr="20px"
                  >
                    {/* Left: program label */}
                    <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
                      {program.label}
                    </Typography>
                    {/* Right: expand icon, then download icon */}
                    <Box display="flex" gap={2} alignItems="center">
                      <IconButton
                        sx={{ color: "#000" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPoliciesMap((prev) => ({
                            ...prev,
                            [code]: !prev[code],
                          }));
                        }}
                      >
                        {showPoliciesMap[code] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                      <IconButton
                        disabled={
                          !programPoliciesMap[code] ||
                          programPoliciesMap[code].length === 0
                        }
                        sx={{
                          color:
                            programPoliciesMap[code] &&
                            programPoliciesMap[code].length > 0
                              ? "#000"
                              : "#ccc",
                          "&.Mui-disabled": {
                            color: "#ccc",
                          },
                        }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await getPolicyByProgramAndLabel({
                              program_code: code,
                            });
                            const policies = res.output?.policy || [];
                            downloadPoliciesPdf(program.label, policies);
                          } catch (err) {
                            setSnackbar({
                              open: true,
                              message: "Failed to fetch policies for download.",
                              severity: "error",
                            });
                          }
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {loadingPoliciesMap[code] ? (
                    <Typography>Loading policies...</Typography>
                  ) : programPoliciesMap[code] &&
                    programPoliciesMap[code].length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      {programPoliciesMap[code].map((policy, idx) => (
                        <Accordion
                          key={policy.label_id || idx}
                          expanded={showPoliciesMap[`${code}-${idx}`] || false}
                          onChange={() => {
                            setShowPoliciesMap((prev) => {
                              const newMap = { ...prev };
                              Object.keys(newMap).forEach((k) => {
                                if (k.startsWith(`${code}-`)) newMap[k] = false;
                              });
                              newMap[`${code}-${idx}`] =
                                !prev[`${code}-${idx}`];
                              return newMap;
                            });
                          }}
                          sx={{
                            bgcolor: "#fff",
                            border: "1px solid #eee",
                            boxShadow: 0,
                            mb: 1,
                            width: "100%",
                          }}
                        >
                          <AccordionSummary
                            sx={{
                              minHeight: 48,
                              "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                              },
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              width="100%"
                            >
                              {/* Left: label */}
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 15,
                                  color: "#000",
                                }}
                              >
                                {policy.label}
                              </Typography>

                              {/* Right: expand -> edit -> delete */}
                              <Box display="flex" gap={1} alignItems="center">
                                <IconButton
                                  sx={{ color: "#000" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Toggle expand
                                    setShowPoliciesMap((prev) => {
                                      const newMap = { ...prev };
                                      Object.keys(newMap).forEach((k) => {
                                        if (k.startsWith(`${code}-`))
                                          newMap[k] = false;
                                      });
                                      newMap[`${code}-${idx}`] =
                                        !prev[`${code}-${idx}`];
                                      return newMap;
                                    });
                                  }}
                                >
                                  {showPoliciesMap[`${code}-${idx}`] ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>

                                <IconButton
                                  sx={{ color: "#000" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(code, idx);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>

                                <IconButton
                                  sx={{ color: "#000" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTarget({
                                      programCode: code,
                                      policy_id: policy.policy_id,
                                    });
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails
                            sx={{
                              maxHeight: "190px",
                              overflowY: "auto",
                            }}
                          >
                            <Typography
                              sx={{ color: "#000", fontSize: 15 }}
                              dangerouslySetInnerHTML={{
                                __html: policy.description,
                              }}
                            />
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      No policies available.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Box>
      {/* Edit Policy Dialog */}
      <EditPolicyDialog
        open={editPolicyDialogOpen}
        onClose={() => setEditPolicyDialogOpen(false)}
        onSave={async (data) => {
          try {
            if (!editPolicyDialogInitialValues) return;
            setEditDialogLoading(true);
            await editPolicy({
              ...data,
              program_code: editPolicyDialogInitialValues.program_code,
              // course_code: "12",
              policy_id: editPolicyDialogInitialValues.policy_id,
            });
            setEditPolicyDialogOpen(false);
            handlePolicyUpdated(editPolicyDialogInitialValues.program_code);
            setSnackbar({
              open: true,
              message: "Policy updated successfully!",
              severity: "success",
            });
          } catch (error: any) {
            let errorMessage = "Failed to update policy.";

            if (error?.message) {
              errorMessage = error.message;
            } else if (typeof error === "string") {
              errorMessage = error;
            }
            setSnackbar({
              open: true,
              message: errorMessage,
              severity: "error",
            });
          } finally {
            setEditDialogLoading(false);
          }
        }}
        initialValues={
          editPolicyDialogInitialValues || {
            label: "",
            description: "",
            programName: "",
          }
        }
        loading={editDialogLoading || editDialogFetchLoading} // <-- show loading spinner if fetching
      />
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Policy"
        description="Are you sure you want to delete this Policy? This action cannot be undone."
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          setDeleteLoading(true);
          try {
            await deletePolicy({
              program_code: deleteTarget.programCode,
              policy_id: deleteTarget.policy_id,
            });
            setSnackbar({
              open: true,
              message: "Policy deleted successfully!",
              severity: "success",
            });
            // Refresh the policy list for the current program
            setLoadingPoliciesMap((prev) => ({
              ...prev,
              [deleteTarget.programCode]: true,
            }));
            const res = await getPolicyByProgramAndLabel({
              program_code: deleteTarget.programCode,
            });
            setProgramPoliciesMap((prev) => ({
              ...prev,
              [deleteTarget.programCode]: res.output?.policy || [],
            }));
            setShowPoliciesMap((prev) => ({
              ...prev,
              [deleteTarget.programCode]: true,
            }));
            setLoadingPoliciesMap((prev) => ({
              ...prev,
              [deleteTarget.programCode]: false,
            }));
          } catch (err) {
            setSnackbar({
              open: true,
              message: "Failed to delete policy.",
              severity: "error",
            });
          } finally {
            setDeleteLoading(false);
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
          }
        }}
      />
      <Snackbar
        open={snackbar.open || snackbarOpen}
        autoHideDuration={3000}
        onClose={() => {
          setSnackbar((prev) => ({ ...prev, open: false }));
          setSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => {
            setSnackbar((prev) => ({ ...prev, open: false }));
            setSnackbarOpen(false);
          }}
          severity={snackbar.severity || "success"}
          sx={{ width: "100%" }}
        >
          {snackbar.message || "Policy updated successfully!"}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PolicyDashboard;
